import React, { useState } from 'react';
import { Camera } from 'lucide-react';
import { useDrag } from '@use-gesture/react';
import styles from './index.module.scss';

interface ScanFABProps {
	onClick: () => void;
	isScanning?: boolean;
}

const FAB_SIZE = 56;
const DRAG_THRESHOLD = 4;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const ScanFAB = ({ onClick, isScanning = false }: ScanFABProps) => {
	const initialY = -(FAB_SIZE / 2) + 220;
	const [position, setPosition] = useState({ x: 0, y: initialY });
	const [isDragging, setIsDragging] = useState(false);

	const bind = useDrag(
		({ first, last, movement: [mx, my], memo = position }) => {
			if (first) setIsDragging(false);

			const hasMoved = Math.abs(mx) > DRAG_THRESHOLD || Math.abs(my) > DRAG_THRESHOLD;
			if (hasMoved) setIsDragging(true);

			const x = clamp(memo.x + mx, -(window.innerWidth - FAB_SIZE - 16), 0);

			const halfVP = window.innerHeight / 2;
			const y = clamp(memo.y + my, -halfVP + FAB_SIZE / 2, halfVP - FAB_SIZE / 2);

			setPosition({ x, y });

			if (last) {
				setIsDragging(false);
				return { x, y };
			}

			return memo;
		},
		{
			filterTaps: true,
			threshold: DRAG_THRESHOLD,
		}
	);

	const handleClick = () => {
		if (isDragging) return;
		onClick();
	};

	return (
		<button
			{...bind()}
			onClick={handleClick}
			className={`${styles.fab} ${isScanning ? styles.active : ''} ${isDragging ? styles.dragging : ''}`}
			style={{
				transform: `translate(${position.x}px, ${position.y}px)`,
			}}
			aria-label="Open barcode scanner"
			title="Tap to scan product barcode"
		>
			<Camera size={28} strokeWidth={2} />
			<span className={styles.tooltip}>Tap to Scan</span>
		</button>
	);
};

export default ScanFAB;
